import React, { useState, useEffect, useCallback } from 'react';
import { Link2, Plus, Copy, BarChart3, Edit2, Trash2, ExternalLink, QrCode, Clock, Lock, ToggleLeft, ToggleRight, X, AlertCircle, Crown, Loader2, MousePointer, Calendar, Users, Globe, Smartphone, Monitor, TrendingUp } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { UserLayout } from '../components/User/UserLayout';
import { useAuthStore } from '../store/authStore';
import { useEffectivePlan } from '../store/accountStore';
import { isPaidTier } from '../constants/planLimits';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
// Public short URLs: https://<short-domain>/<code> (see server GET /:code).
// Override host via VITE_SHORT_LINK_BASE per environment (e.g. staging).
const SHORT_LINK_BASE =
  import.meta.env.VITE_SHORT_LINK_BASE || 'https://hitzz.cc';

interface ShortLink {
  id: string;
  short_code: string;
  target_url: string;
  title: string | null;
  clicks: number;
  is_active: boolean;
  password_hash: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

interface LinkStats {
  totalLinks: number;
  activeLinks: number;
  totalClicks: number;
  uniqueClicks: number;
  todayClicks: number;
  perLinkUnique: Record<string, number>;
}

export const ShortLinks: React.FC = () => {
  const { t } = useTranslation('shortLinks');
  const { token } = useAuthStore();
  const [links, setLinks] = useState<ShortLink[]>([]);
  const [stats, setStats] = useState<LinkStats>({ totalLinks: 0, activeLinks: 0, totalClicks: 0, uniqueClicks: 0, todayClicks: 0, perLinkUnique: {} });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedLink, setSelectedLink] = useState<ShortLink | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [linkAnalytics, setLinkAnalytics] = useState<any>(null);
  const [statsTimeRange, setStatsTimeRange] = useState<string>('all');
  const [statsCustomStart, setStatsCustomStart] = useState<string>('');
  const [statsCustomEnd, setStatsCustomEnd] = useState<string>('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  
  // Workspace owner's plan — not the member's personal Free tier.
  const effectivePlan = useEffectivePlan();
  const isPaidPlan = isPaidTier(effectivePlan);

  // Form state
  // `passwordEnabled` is a separate toggle from `password` so the user
  // has explicit control over whether the link is protected at all.
  // Previously a stray `<input type="password">` got silently filled
  // by browser / 1Password autofill with the user's own login password
  // — turning every freshly-created short link into a protected one.
  const [formData, setFormData] = useState({
    target_url: '',
    title: '',
    custom_code: '',
    expires_at: '',
    passwordEnabled: false,
    password: '',
  });

  useEffect(() => {
    fetchLinks();
    fetchStats();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await fetch(`${API_URL}/shortlinks`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLinks(data);
      }
    } catch (error) {
      console.error('Failed to fetch links:', error);
      toast.error(t('toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/shortlinks/stats/summary`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.target_url) {
      toast.error(t('toast.enterUrl'));
      return;
    }

    // Only forward `password` when the toggle is explicitly ON and
    // there's a non-empty value. Anything else (toggle off, empty
    // input, whitespace-only autofill) becomes a normal unprotected
    // link — never silently protected.
    const trimmed = formData.password.trim();
    const payload = {
      target_url: formData.target_url,
      title: formData.title,
      custom_code: formData.custom_code,
      expires_at: formData.expires_at,
      password: formData.passwordEnabled && trimmed ? trimmed : undefined,
    };

    try {
      const response = await fetch(`${API_URL}/shortlinks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.upgrade_required) {
          toast.error(data.error);
        } else {
          throw new Error(data.error);
        }
        return;
      }

      toast.success(t('toast.createSuccess'));
      setShowCreateModal(false);
      setFormData({ target_url: '', title: '', custom_code: '', expires_at: '', passwordEnabled: false, password: '' });
      fetchLinks();
      fetchStats();
    } catch (error: any) {
      toast.error(error.message || t('toast.createFailed'));
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLink) return;

    // Toggle is the source of truth on edit too:
    //   * OFF → always clear any existing password (no autofill foot-gun)
    //   * ON  + non-empty input → set the new password
    //   * ON  + empty input → keep whatever's already saved
    const trimmed = formData.password.trim();
    const passwordEnabled = formData.passwordEnabled;
    const hadPassword = !!selectedLink.password_hash;
    let passwordField: string | undefined = undefined;
    let removePassword = false;
    if (!passwordEnabled) {
      // Only send remove_password when there's actually something to
      // clear so we don't write to the column on every save.
      if (hadPassword) removePassword = true;
    } else if (trimmed) {
      passwordField = trimmed;
    }

    try {
      const response = await fetch(`${API_URL}/shortlinks/${selectedLink.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          target_url: formData.target_url,
          title: formData.title,
          expires_at: formData.expires_at || null,
          password: passwordField,
          remove_password: removePassword || undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error);
      }

      toast.success(t('toast.updateSuccess'));
      setShowEditModal(false);
      fetchLinks();
    } catch (error: any) {
      toast.error(error.message || t('toast.updateFailed'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('toast.deleteConfirm'))) return;

    try {
      const response = await fetch(`${API_URL}/shortlinks/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast.success(t('toast.deleteSuccess'));
      fetchLinks();
      fetchStats();
    } catch (error) {
      toast.error(t('toast.deleteFailed'));
    }
  };

  const handleToggleActive = async (link: ShortLink) => {
    try {
      const response = await fetch(`${API_URL}/shortlinks/${link.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ is_active: !link.is_active }),
      });

      if (!response.ok) throw new Error('Failed to update');

      toast.success(link.is_active ? t('toast.linkDisabled') : t('toast.linkEnabled'));
      fetchLinks();
      fetchStats();
    } catch (error) {
      toast.error(t('toast.toggleFailed'));
    }
  };

  const copyToClipboard = (code: string) => {
    const shortUrl = `${SHORT_LINK_BASE}/${code}`;
    navigator.clipboard.writeText(shortUrl);
    toast.success(t('toast.copied'));
  };

  const showQR = async (link: ShortLink) => {
    const shortUrl = `${SHORT_LINK_BASE}/${link.short_code}`;
    const qr = await QRCode.toDataURL(shortUrl, { width: 300, margin: 2 });
    setQrCodeUrl(qr);
    setSelectedLink(link);
    setShowQRModal(true);
  };

  const fetchLinkAnalytics = useCallback(async (linkId: string, range: string, customStart?: string, customEnd?: string) => {
    setIsLoadingStats(true);
    try {
      let url = `${API_URL}/shortlinks/${linkId}?timeRange=${range}`;
      if (range === 'custom' && customStart && customEnd) {
        url += `&startDate=${customStart}&endDate=${customEnd}`;
      }
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLinkAnalytics(data);
      }
    } catch (error) {
      toast.error(t('toast.analyticsLoadFailed'));
    } finally {
      setIsLoadingStats(false);
    }
  }, [token, t]);

  const showStats = async (link: ShortLink) => {
    setSelectedLink(link);
    setStatsTimeRange('all');
    setShowCustomDatePicker(false);
    setShowStatsModal(true);
    await fetchLinkAnalytics(link.id, 'all');
  };

  const handleStatsTimeChange = async (range: string) => {
    if (!selectedLink) return;
    setStatsTimeRange(range);
    if (range === 'custom') {
      setShowCustomDatePicker(true);
      return;
    }
    setShowCustomDatePicker(false);
    await fetchLinkAnalytics(selectedLink.id, range);
  };

  const handleCustomDateApply = async () => {
    if (!selectedLink || !statsCustomStart || !statsCustomEnd) return;
    await fetchLinkAnalytics(selectedLink.id, 'custom', statsCustomStart, statsCustomEnd);
  };

  const openEditModal = (link: ShortLink) => {
    setSelectedLink(link);
    setFormData({
      target_url: link.target_url,
      title: link.title || '',
      custom_code: link.short_code,
      expires_at: link.expires_at ? new Date(link.expires_at).toISOString().slice(0, 16) : '',
      // Seed toggle from saved state. Password input itself stays
      // empty — we never round-trip the hash through the FE, so the
      // user types a NEW value to change it (or flips the toggle off
      // to clear it).
      passwordEnabled: !!link.password_hash,
      password: '',
    });
    setShowEditModal(true);
  };

  const getShortUrl = (code: string) => `${SHORT_LINK_BASE}/${code}`;

  return (
    <UserLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Link2 className="w-8 h-8 text-purple-600" />
              {t('title')}
            </h1>
            <p className="text-gray-600 mt-1">{t('subtitle')}</p>
          </div>
          <button
            onClick={() => {
              setFormData({ target_url: '', title: '', custom_code: '', expires_at: '', passwordEnabled: false, password: '' });
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
          >
            <Plus className="w-5 h-5" />
            {t('newLink')}
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">{t('stats.totalLinks')}</p>
              <Link2 className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.totalLinks}</p>
            <p className="text-xs text-purple-600 mt-1">{t('stats.activeCount', { value: stats.activeLinks })}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">{t('stats.totalClicks')}</p>
              <MousePointer className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.totalClicks.toLocaleString()}</p>
            <p className="text-xs text-blue-600 mt-1">{t('stats.allTime')}</p>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-teal-700 uppercase tracking-wide">{t('stats.uniqueClicks')}</p>
              <Users className="w-5 h-5 text-teal-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.uniqueClicks.toLocaleString()}</p>
            <p className="text-xs text-teal-600 mt-1">{t('stats.uniqueVisitors')}</p>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-orange-700 uppercase tracking-wide">{t('stats.today')}</p>
              <TrendingUp className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.todayClicks.toLocaleString()}</p>
            <p className="text-xs text-orange-600 mt-1">{t('stats.clicksToday')}</p>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-lime-50 border border-green-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-green-700 uppercase tracking-wide">{t('stats.ctr')}</p>
              <BarChart3 className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {stats.totalClicks > 0 ? ((stats.uniqueClicks / stats.totalClicks) * 100).toFixed(1) : '0.0'}%
            </p>
            <p className="text-xs text-green-600 mt-1">{t('stats.uniqueRate')}</p>
          </div>
        </div>

        {/* Free Plan Limit Notice */}
        {!isPaidPlan && (
          <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <span className="text-amber-800">
                <Trans
                  i18nKey="freePlan.notice"
                  ns="shortLinks"
                  values={{ used: stats.totalLinks }}
                  components={{ strong: <strong /> }}
                />
              </span>
            </div>
            <a href="/upgrade" className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity">
              <Crown className="w-4 h-4" />
              {t('freePlan.upgrade')}
            </a>
          </div>
        )}

        {/* Links List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : links.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Link2 className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('empty.title')}</h3>
            <p className="text-gray-600 mb-6">{t('empty.subtitle')}</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-colors"
            >
              <Plus className="w-5 h-5" />
              {t('empty.cta')}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {links.map((link) => (
              <div
                key={link.id}
                className={`bg-white rounded-2xl shadow-sm border p-6 transition-all hover:shadow-md ${
                  !link.is_active ? 'border-gray-200 opacity-60' : 'border-gray-100'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {link.title || t('linkCard.untitled')}
                      </h3>
                      {link.password_hash && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                          <Lock className="w-3 h-3" />
                          {t('linkCard.protected')}
                        </span>
                      )}
                      {link.expires_at && new Date(link.expires_at) > new Date() && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                          <Clock className="w-3 h-3" />
                          {t('linkCard.expires', { date: new Date(link.expires_at).toLocaleDateString() })}
                        </span>
                      )}
                      {!link.is_active && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                          {t('linkCard.disabled')}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <a
                        href={getShortUrl(link.short_code)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                      >
                        {getShortUrl(link.short_code)}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    
                    <p className="text-sm text-gray-500 truncate">
                      → {link.target_url}
                    </p>

                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center gap-3 px-3 py-1.5 bg-purple-50 rounded-lg">
                        <MousePointer className="w-3.5 h-3.5 text-purple-600" />
                        <span className="text-sm font-semibold text-purple-700">{link.clicks.toLocaleString()}</span>
                        <span className="text-xs text-purple-500">{t('linkCard.clicksLabel')}</span>
                      </div>
                      <div className="flex items-center gap-3 px-3 py-1.5 bg-teal-50 rounded-lg">
                        <Users className="w-3.5 h-3.5 text-teal-600" />
                        <span className="text-sm font-semibold text-teal-700">{(stats.perLinkUnique[link.id] || 0).toLocaleString()}</span>
                        <span className="text-xs text-teal-500">{t('linkCard.uniqueLabel')}</span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {t('linkCard.createdLabel', { date: new Date(link.created_at).toLocaleDateString() })}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => copyToClipboard(link.short_code)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title={t('actions.copy')}
                    >
                      <Copy className="w-5 h-5 text-gray-600" />
                    </button>
                    <button
                      onClick={() => showQR(link)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title={t('actions.qr')}
                    >
                      <QrCode className="w-5 h-5 text-gray-600" />
                    </button>
                    <button
                      onClick={() => showStats(link)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title={t('actions.analytics')}
                    >
                      <BarChart3 className="w-5 h-5 text-gray-600" />
                    </button>
                    <button
                      onClick={() => openEditModal(link)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title={t('actions.edit')}
                    >
                      <Edit2 className="w-5 h-5 text-gray-600" />
                    </button>
                    <button
                      onClick={() => handleToggleActive(link)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title={link.is_active ? t('actions.disable') : t('actions.enable')}
                    >
                      {link.is_active ? (
                        <ToggleRight className="w-5 h-5 text-green-600" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDelete(link.id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      title={t('actions.delete')}
                    >
                      <Trash2 className="w-5 h-5 text-red-500" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">{t('create.title')}</h2>
                <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleCreate} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('create.destinationUrl')}
                  </label>
                  <input
                    type="url"
                    value={formData.target_url}
                    onChange={(e) => setFormData({ ...formData, target_url: e.target.value })}
                    placeholder={t('create.destinationPlaceholder')}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('create.linkTitle')}
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={t('create.linkTitlePlaceholder')}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    {t('create.customCode')}
                    {!isPaidPlan && <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">{t('create.proBadge')}</span>}
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">{SHORT_LINK_BASE}/</span>
                    <input
                      type="text"
                      value={formData.custom_code}
                      onChange={(e) => setFormData({ ...formData, custom_code: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      placeholder={t('create.customCodePlaceholder')}
                      disabled={!isPaidPlan}
                      className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    {t('create.expirationDate')}
                    {!isPaidPlan && <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">{t('create.proBadge')}</span>}
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    disabled={!isPaidPlan}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>

                {/* Password protection: explicit toggle + input. The
                    input only renders when the toggle is ON, so the
                    browser / 1Password can't silently autofill into a
                    hidden field. Even when shown, autoComplete="new-password"
                    + the 1P/LP ignore attrs stop saved logins from
                    being suggested for *this* hitzlink.com page. */}
                <div>
                  <label className="flex items-center justify-between gap-3 cursor-pointer select-none">
                    <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Lock className="w-4 h-4 text-gray-500" />
                      {t('create.passwordProtection')}
                      {!isPaidPlan && <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">{t('create.proBadge')}</span>}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        if (!isPaidPlan) return;
                        setFormData({
                          ...formData,
                          passwordEnabled: !formData.passwordEnabled,
                          // Clear value when turning OFF so we don't carry
                          // a stale password into the next save.
                          password: formData.passwordEnabled ? '' : formData.password,
                        });
                      }}
                      disabled={!isPaidPlan}
                      aria-pressed={formData.passwordEnabled}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        formData.passwordEnabled ? 'bg-purple-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.passwordEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </label>
                  {formData.passwordEnabled && (
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder={t('create.passwordPlaceholder')}
                      disabled={!isPaidPlan}
                      autoComplete="new-password"
                      // 1Password / LastPass / Bitwarden opt-outs so a
                      // saved hitzlink.com login can't get suggested for
                      // this completely-unrelated "set a link password"
                      // field — which is what caused the autofill bug
                      // in the first place.
                      data-1p-ignore
                      data-lpignore="true"
                      data-form-type="other"
                      className="mt-2 w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                    />
                  )}
                  {!formData.passwordEnabled && (
                    <p className="mt-1.5 text-xs text-gray-500">{t('create.passwordOffHint')}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  {t('create.submit')}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && selectedLink && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">{t('edit.title')}</h2>
                <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleUpdate} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('edit.shortUrl')}
                  </label>
                  <p className="text-purple-600 font-medium">{getShortUrl(selectedLink.short_code)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('edit.destinationUrl')}
                  </label>
                  <input
                    type="url"
                    value={formData.target_url}
                    onChange={(e) => setFormData({ ...formData, target_url: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('edit.linkTitle')}
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {isPaidPlan && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('edit.expirationDate')}
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.expires_at}
                        onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>

                    {/* Password toggle: turning OFF clears the saved
                        password on next save. Leaving it ON with the
                        input blank keeps whatever's already saved. */}
                    <div>
                      <label className="flex items-center justify-between gap-3 cursor-pointer select-none">
                        <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <Lock className="w-4 h-4 text-gray-500" />
                          {t('edit.passwordProtection')}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              passwordEnabled: !formData.passwordEnabled,
                              password: formData.passwordEnabled ? '' : formData.password,
                            })
                          }
                          aria-pressed={formData.passwordEnabled}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            formData.passwordEnabled ? 'bg-purple-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.passwordEnabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </label>
                      {formData.passwordEnabled ? (
                        <>
                          <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            placeholder={selectedLink.password_hash ? t('edit.passwordPlaceholderKeep') : t('edit.passwordPlaceholderOptional')}
                            autoComplete="new-password"
                            data-1p-ignore
                            data-lpignore="true"
                            data-form-type="other"
                            className="mt-2 w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                          {selectedLink.password_hash && (
                            <p className="text-xs text-gray-500 mt-1">{t('edit.passwordKeepHint')}</p>
                          )}
                        </>
                      ) : (
                        <p className="mt-1.5 text-xs text-gray-500">
                          {selectedLink.password_hash
                            ? t('edit.passwordWillBeCleared')
                            : t('edit.passwordOffHint')}
                        </p>
                      )}
                    </div>
                  </>
                )}

                <button
                  type="submit"
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  {t('edit.submit')}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* QR Code Modal */}
        {showQRModal && selectedLink && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">{t('qr.title')}</h2>
                <button onClick={() => setShowQRModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 flex flex-col items-center">
                <img src={qrCodeUrl} alt={t('qr.alt')} className="mb-4" />
                <p className="text-sm text-gray-600 mb-4 text-center break-all">
                  {getShortUrl(selectedLink.short_code)}
                </p>
                <a
                  href={qrCodeUrl}
                  download={`qr-${selectedLink.short_code}.png`}
                  className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
                >
                  {t('qr.download')}
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Stats Modal - Enhanced */}
        {showStatsModal && selectedLink && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10 rounded-t-2xl">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                    {t('analyticsModal.title')}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">{linkAnalytics?.title || selectedLink.title || t('analyticsModal.untitled')}</p>
                  <a href={getShortUrl(selectedLink.short_code)} target="_blank" rel="noopener noreferrer" className="text-xs text-purple-600 hover:underline">
                    {getShortUrl(selectedLink.short_code)}
                  </a>
                </div>
                <button onClick={() => setShowStatsModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6">
                {/* Date Filter */}
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  {[
                    { key: 'today', label: t('analyticsModal.filter.today') },
                    { key: 'yesterday', label: t('analyticsModal.filter.yesterday') },
                    { key: '7d', label: t('analyticsModal.filter.last7') },
                    { key: '30d', label: t('analyticsModal.filter.last30') },
                    { key: '90d', label: t('analyticsModal.filter.last90') },
                    { key: 'all', label: t('analyticsModal.filter.all') },
                    { key: 'custom', label: t('analyticsModal.filter.custom') },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => handleStatsTimeChange(key)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        statsTimeRange === key
                          ? 'bg-purple-600 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* Custom Date Picker */}
                {showCustomDatePicker && (
                  <div className="flex items-center gap-3 mb-6 p-4 bg-gray-50 rounded-xl">
                    <input
                      type="date"
                      value={statsCustomStart}
                      onChange={(e) => setStatsCustomStart(e.target.value)}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                    <span className="text-gray-500">{t('analyticsModal.filter.to')}</span>
                    <input
                      type="date"
                      value={statsCustomEnd}
                      onChange={(e) => setStatsCustomEnd(e.target.value)}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                    <button
                      onClick={handleCustomDateApply}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700"
                    >
                      {t('analyticsModal.filter.apply')}
                    </button>
                  </div>
                )}

                {isLoadingStats ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                  </div>
                ) : linkAnalytics ? (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-medium text-purple-700">{t('analyticsModal.summary.totalClicks')}</p>
                          <MousePointer className="w-4 h-4 text-purple-500" />
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{(linkAnalytics.analytics?.totalClicks || 0).toLocaleString()}</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-medium text-blue-700">{t('analyticsModal.summary.uniqueClicks')}</p>
                          <Users className="w-4 h-4 text-blue-500" />
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{(linkAnalytics.analytics?.uniqueClicks || 0).toLocaleString()}</p>
                      </div>
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-medium text-green-700">{t('analyticsModal.summary.countries')}</p>
                          <Globe className="w-4 h-4 text-green-500" />
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{Object.keys(linkAnalytics.analytics?.countryDistribution || {}).length}</p>
                      </div>
                      <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-medium text-orange-700">{t('analyticsModal.summary.sources')}</p>
                          <TrendingUp className="w-4 h-4 text-orange-500" />
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{Object.keys(linkAnalytics.analytics?.referrerDistribution || {}).length}</p>
                      </div>
                    </div>

                    {/* Daily Clicks Chart */}
                    {(linkAnalytics.analytics?.dailyData || []).length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-purple-600" />
                          {t('analyticsModal.chart.title')}
                        </h4>
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={linkAnalytics.analytics.dailyData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="date" stroke="#6b7280" tick={{ fontSize: 11 }} />
                            <YAxis stroke="#6b7280" tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Bar dataKey="clicks" fill="#8b5cf6" name={t('analyticsModal.chart.totalClicksLabel')} radius={[4, 4, 0, 0]} />
                            <Bar dataKey="uniqueClicks" fill="#3b82f6" name={t('analyticsModal.chart.uniqueClicksLabel')} radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Device + Country Row */}
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      {/* Device Pie Chart */}
                      <div className="bg-white border border-gray-200 rounded-xl p-5">
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Smartphone className="w-4 h-4 text-blue-600" />
                          {t('analyticsModal.deviceDistribution')}
                        </h4>
                        {(() => {
                          const devData = Object.entries(linkAnalytics.analytics?.deviceDistribution || {});
                          if (devData.length === 0) return <p className="text-sm text-gray-400 text-center py-8">{t('analyticsModal.noData')}</p>;
                          const total = devData.reduce((s, [, c]) => s + (c as number), 0);
                          const deviceColors: Record<string, string> = { Mobile: '#3b82f6', Desktop: '#8b5cf6', Tablet: '#f59e0b', Unknown: '#6b7280' };
                          const pieData = devData.map(([name, count]) => ({
                            name,
                            value: count as number,
                            color: deviceColors[name] || '#10b981',
                          }));
                          return (
                            <>
                              <ResponsiveContainer width="100%" height={160}>
                                <PieChart>
                                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={5} dataKey="value">
                                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                  </Pie>
                                  <Tooltip />
                                </PieChart>
                              </ResponsiveContainer>
                              <div className="space-y-2 mt-2">
                                {pieData.map((item, i) => (
                                  <div key={i} className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                                      <span className="text-sm text-gray-600">{item.name}</span>
                                    </div>
                                    <span className="text-sm font-semibold text-gray-900">{item.value} ({total > 0 ? Math.round((item.value / total) * 100) : 0}%)</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          );
                        })()}
                      </div>

                      {/* Countries */}
                      <div className="bg-white border border-gray-200 rounded-xl p-5">
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Globe className="w-4 h-4 text-orange-600" />
                          {t('analyticsModal.topCountries')}
                        </h4>
                        {(() => {
                          const countries = Object.entries(linkAnalytics.analytics?.countryDistribution || {})
                            .sort((a, b) => (b[1] as number) - (a[1] as number))
                            .slice(0, 8);
                          if (countries.length === 0) return <p className="text-sm text-gray-400 text-center py-8">{t('analyticsModal.noData')}</p>;
                          const maxCount = countries[0]?.[1] as number || 1;
                          const flags: Record<string, string> = {
                            'Thailand': '\u{1F1F9}\u{1F1ED}', 'United States': '\u{1F1FA}\u{1F1F8}', 'United Kingdom': '\u{1F1EC}\u{1F1E7}',
                            'Japan': '\u{1F1EF}\u{1F1F5}', 'China': '\u{1F1E8}\u{1F1F3}', 'South Korea': '\u{1F1F0}\u{1F1F7}', 'Singapore': '\u{1F1F8}\u{1F1EC}',
                            'Malaysia': '\u{1F1F2}\u{1F1FE}', 'Vietnam': '\u{1F1FB}\u{1F1F3}', 'Indonesia': '\u{1F1EE}\u{1F1E9}', 'Philippines': '\u{1F1F5}\u{1F1ED}',
                            'Australia': '\u{1F1E6}\u{1F1FA}', 'India': '\u{1F1EE}\u{1F1F3}', 'Germany': '\u{1F1E9}\u{1F1EA}', 'France': '\u{1F1EB}\u{1F1F7}',
                          };
                          return (
                            <div className="space-y-2.5">
                              {countries.map(([country, count], i) => (
                                <div key={i}>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium text-gray-700">
                                      {flags[country] || '\u{1F30D}'} {country}
                                    </span>
                                    <span className="text-xs text-gray-500">{(count as number).toLocaleString()}</span>
                                  </div>
                                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                                    <div className="bg-gradient-to-r from-orange-400 to-orange-600 h-1.5 rounded-full" style={{ width: `${((count as number) / maxCount) * 100}%` }} />
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    {/* OS + Browser + Device Models */}
                    <div className="grid md:grid-cols-3 gap-6 mb-6">
                      {/* OS */}
                      <div className="bg-white border border-gray-200 rounded-xl p-5">
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Monitor className="w-4 h-4 text-blue-600" />
                          {t('analyticsModal.operatingSystems')}
                        </h4>
                        {(() => {
                          const items = Object.entries(linkAnalytics.analytics?.osDistribution || {})
                            .sort((a, b) => (b[1] as number) - (a[1] as number)).slice(0, 8);
                          const total = items.reduce((s, [, c]) => s + (c as number), 0);
                          if (items.length === 0) return <p className="text-sm text-gray-400 text-center py-4">{t('analyticsModal.noData')}</p>;
                          return (
                            <div className="space-y-2.5">
                              {items.map(([name, count], i) => {
                                const pct = total > 0 ? Math.round(((count as number) / total) * 100) : 0;
                                return (
                                  <div key={i}>
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-sm text-gray-700 truncate max-w-[120px]">{name}</span>
                                      <span className="text-xs text-gray-500">{count as number} ({pct}%)</span>
                                    </div>
                                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                                      <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Browsers */}
                      <div className="bg-white border border-gray-200 rounded-xl p-5">
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Globe className="w-4 h-4 text-purple-600" />
                          {t('analyticsModal.browsers')}
                        </h4>
                        {(() => {
                          const items = Object.entries(linkAnalytics.analytics?.browserDistribution || {})
                            .sort((a, b) => (b[1] as number) - (a[1] as number)).slice(0, 8);
                          const total = items.reduce((s, [, c]) => s + (c as number), 0);
                          if (items.length === 0) return <p className="text-sm text-gray-400 text-center py-4">{t('analyticsModal.noData')}</p>;
                          return (
                            <div className="space-y-2.5">
                              {items.map(([name, count], i) => {
                                const pct = total > 0 ? Math.round(((count as number) / total) * 100) : 0;
                                return (
                                  <div key={i}>
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-sm text-gray-700">{name}</span>
                                      <span className="text-xs text-gray-500">{count as number} ({pct}%)</span>
                                    </div>
                                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                                      <div className="bg-purple-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Device Models */}
                      <div className="bg-white border border-gray-200 rounded-xl p-5">
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Smartphone className="w-4 h-4 text-green-600" />
                          {t('analyticsModal.deviceModels')}
                        </h4>
                        {(() => {
                          const items = Object.entries(linkAnalytics.analytics?.deviceModelDistribution || {})
                            .sort((a, b) => (b[1] as number) - (a[1] as number)).slice(0, 8);
                          const total = items.reduce((s, [, c]) => s + (c as number), 0);
                          if (items.length === 0) return <p className="text-sm text-gray-400 text-center py-4">{t('analyticsModal.deviceModelsEmpty')}</p>;
                          return (
                            <div className="space-y-2.5">
                              {items.map(([name, count], i) => {
                                const pct = total > 0 ? Math.round(((count as number) / total) * 100) : 0;
                                return (
                                  <div key={i}>
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-sm text-gray-700 truncate max-w-[120px]" title={name}>{name}</span>
                                      <span className="text-xs text-gray-500">{count as number} ({pct}%)</span>
                                    </div>
                                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                                      <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Traffic Sources */}
                    <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-indigo-600" />
                        {t('analyticsModal.trafficSources')}
                      </h4>
                      {(() => {
                        const items = Object.entries(linkAnalytics.analytics?.referrerDistribution || {})
                          .sort((a, b) => (b[1] as number) - (a[1] as number)).slice(0, 10);
                        const total = items.reduce((s, [, c]) => s + (c as number), 0);
                        if (items.length === 0) return <p className="text-sm text-gray-400 text-center py-4">{t('analyticsModal.noData')}</p>;
                        return (
                          <div className="grid md:grid-cols-2 gap-3">
                            {items.map(([name, count], i) => {
                              const pct = total > 0 ? Math.round(((count as number) / total) * 100) : 0;
                              return (
                                <div key={i} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg">
                                  <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full font-bold text-xs">
                                    {i + 1}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                                    <p className="text-xs text-gray-500">{t('analyticsModal.trafficSourceItem', { value: count as number, percent: pct })}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Recent Clicks Table */}
                    {(linkAnalytics.analytics?.recentClicks || []).length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-xl p-5">
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-600" />
                          {t('analyticsModal.recentClicks')}
                        </h4>
                        <div className="overflow-x-auto">
                          <div className="max-h-64 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">{t('analyticsModal.table.time')}</th>
                                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">{t('analyticsModal.table.device')}</th>
                                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">{t('analyticsModal.table.browser')}</th>
                                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">{t('analyticsModal.table.os')}</th>
                                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">{t('analyticsModal.table.country')}</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {linkAnalytics.analytics.recentClicks.map((click: any, idx: number) => (
                                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                                      {new Date(click.created_at).toLocaleString('th-TH', { 
                                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                                      })}
                                    </td>
                                    <td className="px-3 py-2.5">
                                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                        click.device_type === 'Mobile' ? 'bg-blue-100 text-blue-700' :
                                        click.device_type === 'Desktop' ? 'bg-purple-100 text-purple-700' :
                                        'bg-gray-100 text-gray-700'
                                      }`}>
                                        {click.device_type || '-'}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2.5 text-gray-900">{click.browser || '-'}</td>
                                    <td className="px-3 py-2.5 text-gray-900">{click.os || '-'}</td>
                                    <td className="px-3 py-2.5 text-gray-900">{click.country || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-center py-20 text-gray-400">
                    {t('analyticsModal.noAnalytics')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </UserLayout>
  );
};

